import {
  Add,
  CheckCircle,
  Delete,
  Error,
  PlayArrow,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';

import {
  CalculationFormulaConfig,
  configurationApi,
  FormulaTestResult,
  MetricDefinitionConfig,
} from '../../services/configuration-api';

interface FormulaConfigurationFormProps {
  initialData?: CalculationFormulaConfig;
  metricDefinitions: MetricDefinitionConfig[];
  onSave: () => void;
  onCancel: () => void;
}

interface FormData {
  metricId: string;
  formula: string;
  variables: Array<{ name: string; description: string }>;
  validationRules: string[];
  testCases: Array<{
    name: string;
    inputs: Array<{ name: string; value: number }>;
    expectedOutput: number;
    tolerance?: number;
  }>;
  enabled: boolean;
}

export const FormulaConfigurationForm: React.FC<
  FormulaConfigurationFormProps
> = ({ initialData, metricDefinitions, onSave, onCancel }) => {
  const [error, setError] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<FormulaTestResult[]>([]);
  const [isTestingFormula, setIsTestingFormula] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    setValue,
  } = useForm<FormData>({
    defaultValues: {
      metricId: initialData?.metricId || '',
      formula: initialData?.formula || '',
      variables: initialData?.variables
        ? Object.entries(initialData.variables).map(([name, description]) => ({
            name,
            description,
          }))
        : [],
      validationRules: initialData?.validationRules || [],
      testCases:
        initialData?.testCases?.map(testCase => ({
          name: testCase.name,
          inputs: Object.entries(testCase.inputs).map(([name, value]) => ({
            name,
            value,
          })),
          expectedOutput: testCase.expectedOutput,
          tolerance: testCase.tolerance,
        })) || [],
      enabled: initialData?.enabled ?? true,
    },
    mode: 'onChange',
  });

  const {
    fields: variableFields,
    append: appendVariable,
    remove: removeVariable,
  } = useFieldArray({
    control,
    name: 'variables',
  });

  const {
    fields: testCaseFields,
    append: appendTestCase,
    remove: removeTestCase,
  } = useFieldArray({
    control,
    name: 'testCases',
  });

  const saveMutation = useMutation({
    mutationFn: (data: CalculationFormulaConfig) =>
      configurationApi.saveFormulaConfiguration(data),
    onSuccess: () => {
      onSave();
    },
    onError: (error: any) => {
      setError(
        error.response?.data?.error || 'Failed to save formula configuration'
      );
    },
  });

  const testMutation = useMutation({
    mutationFn: (id: string) => configurationApi.testFormulaConfiguration(id),
    onSuccess: results => {
      setTestResults(results);
    },
    onError: (error: any) => {
      setError(error.response?.data?.error || 'Failed to test formula');
    },
  });

  const onSubmit = (data: FormData) => {
    setError(null);

    // Convert form data to API format
    const variables: Record<string, string> = {};
    data.variables.forEach(variable => {
      variables[variable.name] = variable.description;
    });

    const testCases = data.testCases.map(testCase => {
      const inputs: Record<string, number> = {};
      testCase.inputs.forEach(input => {
        inputs[input.name] = input.value;
      });

      return {
        name: testCase.name,
        inputs,
        expectedOutput: testCase.expectedOutput,
        tolerance: testCase.tolerance,
      };
    });

    const formulaConfig: CalculationFormulaConfig = {
      ...data,
      variables,
      testCases,
      id: initialData?.id,
      createdAt: initialData?.createdAt,
      updatedAt: initialData?.updatedAt,
      version: initialData?.version,
    };

    saveMutation.mutate(formulaConfig);
  };

  const handleTestFormula = async () => {
    if (!initialData?.id) {
      setError('Save the formula first before testing');
      return;
    }

    setIsTestingFormula(true);
    try {
      await testMutation.mutateAsync(initialData.id);
    } finally {
      setIsTestingFormula(false);
    }
  };

  const addVariable = () => {
    appendVariable({ name: '', description: '' });
  };

  const addTestCase = () => {
    appendTestCase({
      name: '',
      inputs: [{ name: '', value: 0 }],
      expectedOutput: 0,
      tolerance: 0.001,
    });
  };

  const addInputToTestCase = (testCaseIndex: number) => {
    const currentTestCase = watch(`testCases.${testCaseIndex}`);
    setValue(`testCases.${testCaseIndex}.inputs`, [
      ...currentTestCase.inputs,
      { name: '', value: 0 },
    ]);
  };

  const removeInputFromTestCase = (
    testCaseIndex: number,
    inputIndex: number
  ) => {
    const currentTestCase = watch(`testCases.${testCaseIndex}`);
    const newInputs = currentTestCase.inputs.filter(
      (_, index) => index !== inputIndex
    );
    setValue(`testCases.${testCaseIndex}.inputs`, newInputs);
  };

  const commonVariables = [
    { name: 'deployments', description: 'Number of deployments' },
    { name: 'timeInDays', description: 'Time period in days' },
    { name: 'timeInHours', description: 'Time period in hours' },
    { name: 'storyPoints', description: 'Story points completed' },
    { name: 'workItems', description: 'Number of work items' },
    { name: 'failures', description: 'Number of failures' },
    { name: 'incidents', description: 'Number of incidents' },
    { name: 'recoveryTime', description: 'Total recovery time' },
  ];

  return (
    <Box component='form' onSubmit={handleSubmit(onSubmit)} sx={{ mt: 2 }}>
      {error && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Basic Configuration */}
        <Grid size={12}>
          <Typography variant='h6' gutterBottom>
            Formula Configuration
          </Typography>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name='metricId'
            control={control}
            rules={{ required: 'Metric is required' }}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.metricId}>
                <InputLabel>Metric</InputLabel>
                <Select {...field} label='Metric'>
                  {metricDefinitions.map(metric => (
                    <MenuItem key={metric.id} value={metric.id}>
                      {metric.name} ({metric.category})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Grid>

        <Grid size={12}>
          <Controller
            name='formula'
            control={control}
            rules={{ required: 'Formula is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                label='Calculation Formula'
                fullWidth
                multiline
                rows={3}
                error={!!errors.formula}
                helperText={
                  errors.formula?.message ||
                  'Use mathematical expressions with variable names'
                }
              />
            )}
          />
        </Grid>

        {/* Variables */}
        <Grid size={12}>
          <Divider sx={{ my: 2 }} />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant='h6'>Variables</Typography>
            <Button
              variant='outlined'
              startIcon={<Add />}
              onClick={addVariable}
            >
              Add Variable
            </Button>
          </Box>

          <Typography variant='body2' color='text.secondary' gutterBottom>
            Common variables:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {commonVariables.map(variable => (
              <Chip
                key={variable.name}
                label={`${variable.name}: ${variable.description}`}
                size='small'
                variant='outlined'
                onClick={() => appendVariable(variable)}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>
        </Grid>

        {variableFields.map((field, index) => (
          <Grid size={12} key={field.id}>
            <Card variant='outlined'>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Controller
                    name={`variables.${index}.name`}
                    control={control}
                    rules={{ required: 'Variable name is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label='Variable Name'
                        size='small'
                        sx={{ minWidth: 150 }}
                      />
                    )}
                  />
                  <Controller
                    name={`variables.${index}.description`}
                    control={control}
                    rules={{ required: 'Description is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label='Description'
                        size='small'
                        fullWidth
                      />
                    )}
                  />
                  <IconButton
                    onClick={() => removeVariable(index)}
                    color='error'
                  >
                    <Delete />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {/* Test Cases */}
        <Grid size={12}>
          <Divider sx={{ my: 2 }} />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant='h6'>Test Cases</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {initialData?.id && (
                <Button
                  variant='outlined'
                  startIcon={<PlayArrow />}
                  onClick={handleTestFormula}
                  disabled={isTestingFormula}
                >
                  {isTestingFormula ? 'Testing...' : 'Test Formula'}
                </Button>
              )}
              <Button
                variant='outlined'
                startIcon={<Add />}
                onClick={addTestCase}
              >
                Add Test Case
              </Button>
            </Box>
          </Box>
        </Grid>

        {testCaseFields.map((field, testCaseIndex) => (
          <Grid size={12} key={field.id}>
            <Card variant='outlined'>
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Controller
                    name={`testCases.${testCaseIndex}.name`}
                    control={control}
                    rules={{ required: 'Test case name is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label='Test Case Name'
                        size='small'
                        sx={{ minWidth: 200 }}
                      />
                    )}
                  />
                  <IconButton
                    onClick={() => removeTestCase(testCaseIndex)}
                    color='error'
                  >
                    <Delete />
                  </IconButton>
                </Box>

                <Typography variant='subtitle2' gutterBottom>
                  Input Values:
                </Typography>
                {watch(`testCases.${testCaseIndex}.inputs`)?.map(
                  (input, inputIndex) => (
                    <Box
                      key={inputIndex}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        mb: 1,
                      }}
                    >
                      <Controller
                        name={`testCases.${testCaseIndex}.inputs.${inputIndex}.name`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label='Variable'
                            size='small'
                            sx={{ minWidth: 120 }}
                          />
                        )}
                      />
                      <Controller
                        name={`testCases.${testCaseIndex}.inputs.${inputIndex}.value`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label='Value'
                            type='number'
                            size='small'
                            sx={{ minWidth: 100 }}
                          />
                        )}
                      />
                      <IconButton
                        onClick={() =>
                          removeInputFromTestCase(testCaseIndex, inputIndex)
                        }
                        size='small'
                        color='error'
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  )
                )}
                <Button
                  size='small'
                  onClick={() => addInputToTestCase(testCaseIndex)}
                >
                  Add Input
                </Button>

                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <Controller
                    name={`testCases.${testCaseIndex}.expectedOutput`}
                    control={control}
                    rules={{ required: 'Expected output is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label='Expected Output'
                        type='number'
                        size='small'
                      />
                    )}
                  />
                  <Controller
                    name={`testCases.${testCaseIndex}.tolerance`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label='Tolerance'
                        type='number'
                        size='small'
                        helperText='Acceptable difference'
                        inputProps={{ step: 0.001 }}
                      />
                    )}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {/* Test Results */}
        {testResults.length > 0 && (
          <Grid size={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant='h6' gutterBottom>
              Test Results
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Test Case</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Expected</TableCell>
                    <TableCell>Actual</TableCell>
                    <TableCell>Error</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {testResults.map((result, index) => (
                    <TableRow key={index}>
                      <TableCell>{result.testCase}</TableCell>
                      <TableCell>
                        {result.passed ? (
                          <Chip
                            icon={<CheckCircle />}
                            label='Passed'
                            color='success'
                            size='small'
                          />
                        ) : (
                          <Chip
                            icon={<Error />}
                            label='Failed'
                            color='error'
                            size='small'
                          />
                        )}
                      </TableCell>
                      <TableCell>{result.expected}</TableCell>
                      <TableCell>{result.actual}</TableCell>
                      <TableCell>{result.error || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        )}

        {/* Settings */}
        <Grid size={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant='h6' gutterBottom>
            Settings
          </Typography>
        </Grid>

        <Grid size={12}>
          <Controller
            name='enabled'
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value} />}
                label='Enable this formula'
              />
            )}
          />
        </Grid>

        {/* Actions */}
        <Grid size={12}>
          <Box
            sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}
          >
            <Button onClick={onCancel} disabled={saveMutation.isPending}>
              Cancel
            </Button>
            <Button
              type='submit'
              variant='contained'
              disabled={!isValid || saveMutation.isPending}
            >
              {saveMutation.isPending ? 'Saving...' : 'Save Formula'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
